import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CompanyModal = ({ company, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !company) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Building2' },
    { id: 'metrics', label: 'Metrics', icon: 'BarChart3' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'team', label: 'Team', icon: 'Users' }
  ];

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Company Header */}
      <div className="flex items-start space-x-6">
        <div className="w-20 h-20 bg-white rounded-brand p-3 shadow-brand-sm flex-shrink-0">
          <Image
            src={company?.logo}
            alt={company?.logoAlt}
            className="w-full h-full object-contain"
          />
        </div>
        
        <div className="flex-1">
          <h2 className="text-3xl font-bold text-foreground mb-2">{company?.name}</h2>
          <p className="text-lg text-muted-foreground mb-4">{company?.tagline}</p>
          
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <span className="text-foreground">{company?.location}</span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-foreground">Founded {company?.founded}</span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <span className="text-foreground">{company?.employees} employees</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-4">About {company?.name}</h3>
        <p className="text-muted-foreground leading-relaxed">{company?.fullDescription}</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {company?.keyMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/30 rounded-brand-sm p-4 text-center">
            <div className="text-2xl font-bold text-foreground mb-1">{metric?.value}</div>
            <div className="text-sm text-muted-foreground">{metric?.label}</div>
          </div>
        ))}
      </div>

      {/* Achievements */}
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-4">Key Achievements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {company?.achievements?.map((achievement, index) => (
            <div key={index} className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <span className="text-muted-foreground">{achievement}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMetrics = () => (
    <div className="space-y-8">
      {/* Performance Chart Placeholder */}
      <div className="bg-muted/20 rounded-brand p-8 text-center">
        <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Performance Dashboard</h3>
        <p className="text-muted-foreground">Interactive charts and metrics visualization would be displayed here</p>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Financial Metrics</h3>
          {company?.financialMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted/20 rounded-brand-sm">
              <span className="text-muted-foreground">{metric?.label}</span>
              <span className="font-semibold text-foreground">{metric?.value}</span>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Growth Metrics</h3>
          {company?.growthMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted/20 rounded-brand-sm">
              <span className="text-muted-foreground">{metric?.label}</span>
              <span className="font-semibold text-foreground">{metric?.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTimeline = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-foreground mb-6">Investment Timeline</h3>
      
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
        
        {company?.timeline?.map((event, index) => (
          <div key={index} className="relative flex items-start space-x-6 pb-8">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 relative z-10">
              <Icon name={event?.icon} size={16} className="text-primary-foreground" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="text-lg font-semibold text-foreground">{event?.title}</h4>
                <span className="text-sm text-muted-foreground">{event?.date}</span>
              </div>
              <p className="text-muted-foreground">{event?.description}</p>
              
              {event?.metrics && (
                <div className="mt-3 flex flex-wrap gap-4">
                  {event?.metrics?.map((metric, metricIndex) => (
                    <div key={metricIndex} className="bg-muted/30 rounded-brand-sm px-3 py-1">
                      <span className="text-sm font-medium text-foreground">{metric}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTeam = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-6">Leadership Team</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {company?.team?.map((member, index) => (
            <div key={index} className="flex items-start space-x-4 p-4 bg-muted/20 rounded-brand">
              <Image
                src={member?.avatar}
                alt={member?.avatarAlt}
                className="w-16 h-16 rounded-full object-cover flex-shrink-0"
              />
              
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-foreground mb-1">{member?.name}</h4>
                <p className="text-sm text-primary mb-2">{member?.role}</p>
                <p className="text-sm text-muted-foreground">{member?.bio}</p>
                
                <div className="flex items-center space-x-3 mt-3">
                  <button className="text-muted-foreground hover:text-primary transition-brand">
                    <Icon name="Linkedin" size={16} />
                  </button>
                  <button className="text-muted-foreground hover:text-primary transition-brand">
                    <Icon name="Twitter" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Founder Quote */}
      {company?.founderQuote && (
        <div className="bg-primary/5 border border-primary/20 rounded-brand p-6">
          <div className="flex items-start space-x-4">
            <Icon name="Quote" size={24} className="text-primary flex-shrink-0 mt-1" />
            <div>
              <p className="text-lg text-foreground italic mb-4">"{company?.founderQuote}"</p>
              <div className="flex items-center space-x-3">
                <Image
                  src={company?.founderAvatar}
                  alt={company?.founderAvatarAlt}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-semibold text-foreground">{company?.founderName}</p>
                  <p className="text-sm text-muted-foreground">{company?.founderTitle}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      {/* Modal */}
      <div className="relative h-full flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-brand shadow-brand-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white rounded-brand p-2 shadow-brand-sm">
                <Image
                  src={company?.logo}
                  alt={company?.logoAlt}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{company?.name}</h1>
                <p className="text-muted-foreground">{company?.industry} • {company?.location}</p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="flex-shrink-0"
            >
              <Icon name="X" size={24} />
            </Button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border">
            <div className="flex space-x-0 px-6">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-brand ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'metrics' && renderMetrics()}
            {activeTab === 'timeline' && renderTimeline()}
            {activeTab === 'team' && renderTeam()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyModal;